import { cn } from '../../utils/cn';
import type { FlashCover as FlashCoverModel, RiskRating } from '../../services/trip-briefs.service';

const RISK_PILL: Record<RiskRating, string> = {
  LOW: 'bg-emerald-600 text-white',
  MEDIUM: 'bg-amber-500 text-white',
  HIGH: 'bg-rose-600 text-white',
};

const RISK_TEXT: Record<RiskRating, string> = {
  LOW: 'text-emerald-700 dark:text-emerald-300',
  MEDIUM: 'text-amber-700 dark:text-amber-300',
  HIGH: 'text-rose-700 dark:text-rose-300',
};

const REC_PILL: Record<string, string> = {
  GO: 'bg-emerald-600 text-white',
  GO_WITH_CAUTION: 'bg-amber-500 text-white',
  NO_GO: 'bg-rose-600 text-white',
};

const REC_LABELS: Record<string, string> = {
  GO: 'GO',
  GO_WITH_CAUTION: 'GO WITH CAUTION',
  NO_GO: 'NO-GO',
};

/** Navy block heading used to open each cover block. */
function BlockHeading({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-t-md bg-[#1B2A4A] px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-white">
      {children}
    </div>
  );
}

/**
 * Renders the structured Executive Flash cover (VIS-144) in the navy/gold
 * corporate palette: risk badge + Go/No-Go banner, hotel/airport/transport
 * header, Block 1 Top Risks, Block 2 Itinerary, Block 3 emergency protocol,
 * Block 4 key contacts.
 */
export function FlashCover({ cover }: { cover: FlashCoverModel }) {
  return (
    <div
      data-testid="flash-cover"
      className="overflow-hidden rounded-xl border-2 border-[#B8960C]/40 bg-white shadow-sm dark:bg-secondary-900"
    >
      {/* Navy banner */}
      <div className="bg-[#1B2A4A] px-5 py-4 text-white">
        <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#E7CE6B]">
          Corporate Security · Executive Protection
        </p>
        <h2 className="mt-1 text-xl font-bold text-white">Executive Flash</h2>
        <p className="mt-0.5 text-sm font-semibold text-[#E7CE6B]">{cover.title}</p>
        <p className="mt-1 text-xs text-secondary-200">
          {cover.protectedPersonName} · {cover.destinations} · {cover.tripWindow}
        </p>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span
            data-testid="flash-cover-risk"
            className={cn('rounded-full px-3 py-1 text-xs font-bold', RISK_PILL[cover.overallRisk])}
          >
            OVERALL RISK: {cover.overallRisk}
          </span>
          {cover.recommendation && (
            <span className={cn('rounded-full px-3 py-1 text-xs font-bold', REC_PILL[cover.recommendation] ?? 'bg-secondary-500 text-white')}>
              {REC_LABELS[cover.recommendation] ?? cover.recommendation}
            </span>
          )}
        </div>
        {cover.recommendationRationale && (
          <p className="mt-2 text-xs italic text-secondary-200">{cover.recommendationRationale}</p>
        )}
      </div>

      <div className="space-y-4 p-5">
        {/* Hotel / airport / transport header */}
        {cover.header.length > 0 && (
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
            {cover.header.map((h) => (
              <span key={h.label}>
                <span className="font-semibold text-[#1B2A4A] dark:text-[#E7CE6B]">{h.label}: </span>
                <span className="text-secondary-700 dark:text-secondary-200">{h.value}</span>
              </span>
            ))}
          </div>
        )}

        {/* Block 1 — Top Risks */}
        <div className="overflow-hidden rounded-md border border-secondary-200 dark:border-secondary-700">
          <BlockHeading>Top Risks</BlockHeading>
          {cover.topRisks.length > 0 ? (
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-[#FBF2D1] text-left text-xs text-[#1B2A4A] dark:bg-secondary-800 dark:text-[#E7CE6B]">
                  <th className="px-3 py-1.5 font-semibold">Area</th>
                  <th className="px-3 py-1.5 font-semibold">Rating</th>
                  <th className="px-3 py-1.5 font-semibold">Assessment</th>
                </tr>
              </thead>
              <tbody>
                {cover.topRisks.map((r, i) => (
                  <tr key={`${r.area}-${i}`} className="border-t border-secondary-100 dark:border-secondary-800">
                    <td className="px-3 py-1.5 font-medium text-secondary-900 dark:text-white">{r.area}</td>
                    <td className={cn('px-3 py-1.5 font-bold', RISK_TEXT[r.rating])}>{r.rating}</td>
                    <td className="px-3 py-1.5 text-secondary-700 dark:text-secondary-200">{r.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="px-3 py-2 text-sm text-secondary-500">No elevated risks identified.</p>
          )}
        </div>

        {/* Block 2 — Itinerary */}
        <div className="overflow-hidden rounded-md border border-secondary-200 dark:border-secondary-700">
          <BlockHeading>Itinerary</BlockHeading>
          {cover.itinerary.length > 0 ? (
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-[#FBF2D1] text-left text-xs text-[#1B2A4A] dark:bg-secondary-800 dark:text-[#E7CE6B]">
                  <th className="px-3 py-1.5 font-semibold">City</th>
                  <th className="px-3 py-1.5 font-semibold">Country</th>
                  <th className="px-3 py-1.5 font-semibold">Dates</th>
                </tr>
              </thead>
              <tbody>
                {cover.itinerary.map((leg, i) => (
                  <tr key={`${leg.city}-${i}`} className="border-t border-secondary-100 dark:border-secondary-800">
                    <td className="px-3 py-1.5 font-medium text-secondary-900 dark:text-white">{leg.city}</td>
                    <td className="px-3 py-1.5 text-secondary-700 dark:text-secondary-200">{leg.country}</td>
                    <td className="px-3 py-1.5 text-secondary-700 dark:text-secondary-200">{leg.dates}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="px-3 py-2 text-sm text-secondary-500">Itinerary to be confirmed.</p>
          )}
        </div>

        {/* Block 3 — If something goes wrong */}
        <div className="overflow-hidden rounded-md border border-secondary-200 dark:border-secondary-700">
          <BlockHeading>If Something Goes Wrong</BlockHeading>
          <ol className="space-y-1.5 px-4 py-3 text-sm">
            {cover.emergencyProtocol.map((step, i) => (
              <li key={i} className="flex gap-2">
                <span className="font-bold text-[#B8960C]">{i + 1}.</span>
                <span className="text-secondary-700 dark:text-secondary-200">{step}</span>
              </li>
            ))}
          </ol>
        </div>

        {/* Block 4 — Key Contacts */}
        <div className="overflow-hidden rounded-md border border-secondary-200 dark:border-secondary-700">
          <BlockHeading>Key Contacts</BlockHeading>
          <dl className="divide-y divide-secondary-100 text-sm dark:divide-secondary-800">
            {cover.keyContacts.map((c, i) => (
              <div key={`${c.label}-${i}`} className="flex flex-wrap items-baseline gap-x-2 px-3 py-1.5">
                <dt className="font-semibold text-[#1B2A4A] dark:text-[#E7CE6B]">{c.label}:</dt>
                <dd className="text-secondary-800 dark:text-secondary-100">
                  {c.value}
                  {c.detail && <span className="ml-1 text-secondary-500">({c.detail})</span>}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </div>
  );
}
